import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { formatDate } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import CustomerCreate from './CustomerCreate';
import { inputClass } from '../components/ui/Input';

interface Relation {
  id: string;
  code: string | null;
  name: string;
  customer_type: string;
  status: string | null;
  buyer_group_id: string | null;
  payment_terms: string | null;
  /** null = no ceiling ever chosen (migration 079) — the accounts this filter surfaces. */
  credit_limit: string | number | null;
  created_at: string;
  address: string | null;
}

/** Relation Portal — list of all relations with view / edit / soft-delete. */
export default function Customers() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Relation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // Migration-079 worklist: relations left on "no ceiling" from when 0 meant unlimited.
  const [noCeiling, setNoCeiling] = useState(false);

  const load = useCallback(
    async (query: string, onlyNoCeiling: boolean) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set('q', query.trim());
        if (onlyNoCeiling) params.set('noCeiling', 'true');
        const qs = params.toString();
        const { data } = await apiClient.get<{ rows: Relation[]; total: number }>(
          `/customers${qs ? `?${qs}` : ''}`
        );
        setRows(data.rows ?? []);
        setTotal(data.total ?? 0);
      } catch (err) {
        showApiError(err);
      } finally {
        setLoading(false);
      }
    },
    [showApiError]
  );

  // Live search: reload a short moment after the user stops typing (also runs the
  // initial load with an empty query on mount), so no Enter / Search click is needed.
  useEffect(() => {
    const t = setTimeout(() => void load(q, noCeiling), 300);
    return () => clearTimeout(t);
  }, [q, noCeiling, load]);

  // Row interaction: a single click views the relation, a double click edits it.
  // We defer the single-click navigation briefly so a double click can cancel it.
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    },
    []
  );

  const onRowClick = (r: Relation) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      navigate(`/customers/${r.id}`);
    }, 220);
  };

  const onRowDoubleClick = (r: Relation) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    navigate(`/customers/${r.id}/edit`);
  };

  const del = async (r: Relation) => {
    const ok = await confirm({
      title: t('customers.confirmDeactivate', { name: r.name }),
      description: t('customers.confirmDeactivateDetail'),
      confirmLabel: t('customers.deactivate'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/customers/${r.id}`, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({ tone: 'success', message: t('customers.deactivated', { name: r.name }) });
      await load(q, noCeiling);
    } catch (err) {
      showApiError(err);
    }
  };

  // Same field surface as every other screen — see components/ui/Input.
  const inputCls = inputClass;

  const th = 'text-left font-medium px-2 py-1.5 whitespace-nowrap';
  const td = 'px-2 py-1.5 whitespace-nowrap';
  const dash = <span className="text-muted-foreground">—</span>;

  return (
    <div>
      {confirmDialog}
      <PageHeader
        title={t('customers.title')}
        description={t('customers.description')}
        actions={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" /> {t('customers.newRelation')}
          </Button>
        }
      />

      <div className="mb-3 flex gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(q, noCeiling)}
          placeholder={t('customers.searchPlaceholder')}
          className={inputCls}
        />
        <Button size="sm" onClick={() => load(q, noCeiling)}>
          <Search className="h-4 w-4" />
          {t('common.search')}
        </Button>
        {/*
          Migration-079 worklist. These relations kept their pre-079 behaviour (no ceiling) on
          purpose — the migration refused to silently convert them into blocked accounts — so
          they need working through by hand. Without a filter, "which ones are left?" is a
          question nobody can answer from this screen.
        */}
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={noCeiling}
            onChange={(e) => setNoCeiling(e.target.checked)}
          />
          {t('customers.noCeiling')}
        </label>
        {(q || noCeiling) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setQ('');
              setNoCeiling(false);
              load('', false);
            }}
          >
            <RotateCcw className="h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground border-b">
            <tr>
              <th className={th}>{t('customers.columns.relationId')}</th>
              <th className={th}>{t('customers.columns.code')}</th>
              <th className={th}>{t('customers.columns.name')}</th>
              <th className={th}>{t('customers.columns.address')}</th>
              <th className={th}>{t('customers.columns.relationGroup')}</th>
              <th className={th}>{t('customers.columns.paymentMethod')}</th>
              <th className={th}>{t('customers.columns.customerType')}</th>
              <th className={th}>{t('customers.columns.creationDate')}</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => onRowClick(r)}
                onDoubleClick={() => onRowDoubleClick(r)}
                className="border-b last:border-0 hover:bg-muted/30 cursor-pointer select-none"
              >
                <td className={`${td} font-mono text-xs`}>{r.code ?? r.id.slice(0, 8)}</td>
                <td className={td}>{r.code ?? dash}</td>
                <td className={td}>
                  <span className="inline-flex items-center gap-1.5 max-w-[18rem]">
                    <span className="truncate" title={r.name}>
                      {r.name}
                    </span>
                    {r.status === 'INACTIVE' && (
                      <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">
                        {t('customers.inactive')}
                      </span>
                    )}
                  </span>
                </td>
                <td className={td}>
                  {r.address ? (
                    <div className="truncate max-w-[18rem]" title={r.address}>
                      {r.address}
                    </div>
                  ) : (
                    dash
                  )}
                </td>
                <td className={td}>{r.buyer_group_id ?? dash}</td>
                <td className={td}>
                  {r.payment_terms ? enumLabel('paymentTerms', r.payment_terms) : dash}
                </td>
                <td className={td}>{enumLabel('customerType', r.customer_type)}</td>
                <td className={td}>{formatDate(r.created_at)}</td>
                <td className={`${td} text-right`}>
                  <span className="inline-flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customers/${r.id}`);
                      }}
                      className="text-muted-foreground hover:text-foreground p-1"
                      title={t('customers.view')}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customers/${r.id}/edit`);
                      }}
                      className="text-muted-foreground hover:text-foreground p-1"
                      title={t('customers.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        del(r);
                      }}
                      className="text-muted-foreground hover:text-destructive p-1"
                      title={t('customers.deactivate')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </span>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="py-6 text-center text-muted-foreground">
                  {t('customers.empty')}
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={12} className="py-6 text-center text-muted-foreground">
                  {t('common.loading')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('customers.count', { shown: rows.length, total })}
        </p>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowCreate(false)}
            aria-hidden
          />
          <aside className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-y-auto rounded-lg border bg-background p-4 shadow-xl">
            <CustomerCreate
              embedded
              onClose={() => setShowCreate(false)}
              onSuccess={() => load(q, noCeiling)}
            />
          </aside>
        </div>
      )}
    </div>
  );
}
